/**
 * City Selector Component for Multi-City Trips
 * Allows users to switch between cities in a multi-city trip
 * Auto-detects current city based on travel dates
 */

import React, { useMemo } from 'react';
import { View, StyleSheet, ScrollView, Pressable, Platform } from 'react-native';
import { useTheme } from '@/theme';
import { ThemedText, Caption } from '@/components/ui/Text';
import { Ionicons } from '@expo/vector-icons';
import type { TripDestination } from '@/services/trips';

export type CitySelectorProps = {
  destinations: TripDestination[];
  selectedCityIndex: number;
  onSelectCity: (index: number) => void;
  showDates?: boolean;
  variant?: 'tabs' | 'pills' | 'dropdown';
};

/**
 * Format a date range for display
 */
const formatDateRange = (startDate: Date, endDate: Date): string => {
  const options: Intl.DateTimeFormatOptions = { 
    month: 'short', 
    day: 'numeric' 
  };
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Same month
  if (start.getMonth() === end.getMonth()) {
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.getDate()}`;
  }
  
  return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
};

/**
 * Determine which city the user is currently in based on today's date
 */
export const getCurrentCityIndex = (destinations: TripDestination[]): number => {
  if (!destinations || destinations.length === 0) return 0;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  for (let i = 0; i < destinations.length; i++) {
    const dest = destinations[i];
    const startDate = new Date(dest.startDate);
    const endDate = new Date(dest.endDate);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    
    // Check if today falls within this destination's dates
    if (today >= startDate && today <= endDate) {
      return i;
    }
  }
  
  // If not currently traveling, find the next upcoming destination
  for (let i = 0; i < destinations.length; i++) {
    const startDate = new Date(destinations[i].startDate);
    startDate.setHours(0, 0, 0, 0);
    
    if (startDate > today) {
      return i;
    }
  }
  
  // Default to first destination
  return 0;
};

/**
 * Check if a city is the current city based on today's date
 */
export const isCityActive = (destination: TripDestination): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const startDate = new Date(destination.startDate);
  const endDate = new Date(destination.endDate);
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);
  
  return today >= startDate && today <= endDate;
};

/**
 * Pills variant - compact horizontal scrollable pills
 */
const PillsSelector: React.FC<CitySelectorProps> = ({
  destinations,
  selectedCityIndex,
  onSelectCity,
  showDates = true,
}) => {
  const theme = useTheme();
  
  return (
    <ScrollView 
      horizontal 
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.pillsContainer}
    >
      {destinations.map((dest, index) => {
        const isSelected = index === selectedCityIndex;
        const isActive = isCityActive(dest);
        
        return (
          <Pressable
            key={`${dest.city}-${index}`}
            onPress={() => onSelectCity(index)}
            style={({ pressed }) => [
              styles.pill,
              {
                backgroundColor: isSelected 
                  ? theme.colors.primary 
                  : theme.colors.surface,
                borderColor: isSelected 
                  ? theme.colors.primary 
                  : theme.colors.borderMuted,
                opacity: pressed ? 0.8 : 1,
              },
            ]}
          >
            {/* Active indicator dot */}
            {isActive && (
              <View 
                style={[
                  styles.activeDot, 
                  { backgroundColor: isSelected ? '#FFFFFF' : theme.colors.success }
                ]} 
              />
            )}
            
            {/* City number */}
            <View 
              style={[
                styles.cityNumber,
                {
                  backgroundColor: isSelected 
                    ? 'rgba(255,255,255,0.2)' 
                    : theme.colors.borderMuted,
                },
              ]}
            >
              <ThemedText 
                variant="caption" 
                style={[
                  styles.cityNumberText,
                  { color: isSelected ? '#FFFFFF' : theme.colors.textSecondary },
                ]}
              >
                {index + 1}
              </ThemedText>
            </View>
            
            {/* City name */}
            <View style={styles.pillContent}>
              <ThemedText 
                variant="bodyStrong" 
                style={[
                  styles.cityName,
                  { color: isSelected ? '#FFFFFF' : theme.colors.text },
                ]}
                numberOfLines={1}
              >
                {dest.city}
              </ThemedText>
              
              {showDates && (
                <Caption 
                  color={isSelected ? 'rgba(255,255,255,0.8)' : theme.colors.textSecondary}
                  style={styles.dateRange}
                  numberOfLines={1}
                >
                  {formatDateRange(dest.startDate, dest.endDate)}
                </Caption>
              )}
            </View>
            
            {/* Chevron for selected */}
            {isSelected && (
              <Ionicons 
                name="chevron-down" 
                size={16} 
                color="rgba(255,255,255,0.8)" 
                style={styles.chevron}
              />
            )}
          </Pressable>
        );
      })}
    </ScrollView>
  );
};

/**
 * Tabs variant - full-width tabs
 */
const TabsSelector: React.FC<CitySelectorProps> = ({
  destinations,
  selectedCityIndex,
  onSelectCity,
  showDates = false,
}) => {
  const theme = useTheme();
  
  return (
    <View style={[styles.tabsContainer, { borderBottomColor: theme.colors.borderMuted }]}>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.tabsScrollContent}
      >
        {destinations.map((dest, index) => {
          const isSelected = index === selectedCityIndex;
          const isActive = isCityActive(dest);
          
          return (
            <Pressable
              key={`${dest.city}-${index}`}
              onPress={() => onSelectCity(index)}
              style={({ pressed }) => [
                styles.tab,
                {
                  borderBottomColor: isSelected ? theme.colors.primary : 'transparent',
                  opacity: pressed ? 0.7 : 1,
                },
              ]}
            >
              <View style={styles.tabContent}>
                {isActive && (
                  <View 
                    style={[
                      styles.activeIndicator, 
                      { backgroundColor: theme.colors.success }
                    ]} 
                  />
                )}
                <ThemedText 
                  variant={isSelected ? 'bodyStrong' : 'body'}
                  style={{ 
                    color: isSelected ? theme.colors.primary : theme.colors.textSecondary,
                  }}
                  numberOfLines={1}
                >
                  {dest.city}
                </ThemedText>
                {showDates && (
                  <Caption 
                    color={theme.colors.textSecondary}
                    style={styles.tabDate}
                  >
                    {formatDateRange(dest.startDate, dest.endDate)}
                  </Caption>
                )}
              </View>
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
};

/**
 * Main CitySelector component
 */
export const CitySelector: React.FC<CitySelectorProps> = (props) => {
  const { variant = 'pills' } = props;
  
  // Don't render if only one or no destinations
  if (!props.destinations || props.destinations.length <= 1) {
    return null;
  }
  
  switch (variant) {
    case 'tabs':
      return <TabsSelector {...props} />;
    case 'pills':
    default:
      return <PillsSelector {...props} />;
  }
};

const styles = StyleSheet.create({
  // Pills styles
  pillsContainer: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    gap: 8,
    flexDirection: 'row',
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    gap: 8,
    // Give iOS enough room to avoid vertical text wrapping
    minWidth: 150,
    maxWidth: 260,
  },
  activeDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    position: 'absolute',
    top: -2,
    right: -2,
  },
  cityNumber: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cityNumberText: {
    fontSize: 11,
    fontWeight: '600',
  },
  pillContent: {
    flex: 1,
    minWidth: 0, // critical for iOS text truncation inside flex rows
  },
  cityName: {
    fontSize: 14,
  },
  dateRange: {
    fontSize: 11,
    marginTop: 1,
  },
  chevron: {
    marginLeft: 4,
  },
  
  // Tabs styles
  tabsContainer: {
    borderBottomWidth: 1,
  },
  tabsScrollContent: {
    paddingHorizontal: 8,
  },
  tab: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 2,
    marginBottom: -1,
  },
  tabContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  activeIndicator: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  tabDate: {
    fontSize: 11,
    marginLeft: 4,
  },
});

export default CitySelector;

